import React, { useEffect, useState } from "react";
import emailjs from "emailjs-com";
import "../css/ContactUs.css";

export default function ContactUs() {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  /* =========================================
     OPEN MODAL
  ========================================= */
  const openPopup = () => {
    setIsPopupOpen(true);
    setIsSubmitted(false);
    setErrorMessage("");
  };

  /* =========================================
     CLOSE MODAL
  ========================================= */
  const closePopup = () => {
    if (isSending) return;

    setIsPopupOpen(false);
    setIsSubmitted(false);
    setErrorMessage("");
  };

  /* =========================================
     ESCAPE KEY + BODY SCROLL LOCK
  ========================================= */
  useEffect(() => {
    if (!isPopupOpen) return undefined;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape" && !isSending) {
        closePopup();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isPopupOpen, isSending]);

  /* =========================================
     SUBMIT
  ========================================= */
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSending) return;

    const form = event.currentTarget;

    const name = form.name.value.trim();
    const email = form.email.value.trim();
    const mobile = form.mobile.value.trim();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^[6-9][0-9]{9}$/;

    setErrorMessage("");

    if (name.length < 2) {
      setErrorMessage("Please enter your name.");
      return;
    }

    if (!emailRegex.test(email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    if (!mobileRegex.test(mobile)) {
      setErrorMessage("Please enter a valid 10-digit mobile number.");
      return;
    }

    try {
      setIsSending(true);

      await emailjs.sendForm(
        "service_l2sb7n6",
        "template_9hy40c6",
        form,
        "B8_kV_-GnbLGy1nSU"
      );

      form.reset();
      setIsSubmitted(true);
    } catch (error) {
      console.error("EmailJS error:", error);

      setErrorMessage(
        "We couldn't send your request right now. Please try again."
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      {/* =====================================================
          TOP ANNOUNCEMENT / CONTACT STRIP
      ====================================================== */}
      <section className="ms-contact-strip">
        <div className="ms-contact-container">
          <div className="ms-contact-message">
            <span className="ms-contact-badge">
              FOR SCHOOLS
            </span>

            <div className="ms-contact-copy">
              <strong>
                Bring future-ready learning to your students.
              </strong>

              <span>
                Explore MindSpark's hands-on Robotics, AI, Coding and STEM
                learning programs.
              </span>
            </div>
          </div>

          <button
            type="button"
            className="ms-demo-button"
            onClick={openPopup}
          >
            <span>Book a Free Demo</span>

            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M5 12h14M13 6l6 6-6 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </section>

      {/* =====================================================
          PROGRAM / LOCATION STRIP
      ====================================================== */}
      <section className="ms-learning-strip">
        <div className="ms-learning-container">
          <div className="ms-learning-item">
            <span className="ms-learning-dot" />
            Robotics
          </div>

          <span className="ms-learning-divider" />

          <div className="ms-learning-item">
            <span className="ms-learning-dot" />
            Artificial Intelligence
          </div>

          <span className="ms-learning-divider" />

          <div className="ms-learning-item">
            <span className="ms-learning-dot" />
            Coding
          </div>

          <span className="ms-learning-divider" />

          <div className="ms-learning-item">
            <span className="ms-learning-dot" />
            Hands-on STEM
          </div>

          <div className="ms-centres">
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M12 21s6-5.33 6-11a6 6 0 1 0-12 0c0 5.67 6 11 6 11Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              />

              <circle
                cx="12"
                cy="10"
                r="2.2"
                fill="currentColor"
              />
            </svg>

            <span>
              Learning Centres:
              <strong>
                Pune & Chhatrapati Sambhajinagar
              </strong>
            </span>
          </div>
        </div>
      </section>

      {/* =====================================================
          MODAL
      ====================================================== */}
      {isPopupOpen && (
        <div
          className="ms-contact-modal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closePopup();
            }
          }}
        >
          <div
            className="ms-contact-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mindspark-demo-title"
          >
            <button
              type="button"
              className="ms-modal-close"
              onClick={closePopup}
              aria-label="Close demo enquiry form"
              disabled={isSending}
            >
              <span />
              <span />
            </button>

            {isSubmitted ? (
              /* =============================================
                 SUCCESS SCREEN
              ============================================== */
              <div className="ms-success-state">
                <div className="ms-success-icon">
                  <svg
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      d="m6 12 4 4 8-9"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                <span className="ms-modal-tag">
                  REQUEST RECEIVED
                </span>

                <h2>Thank you for reaching out!</h2>

                <p>
                  Your demo request has been submitted successfully.
                  The MindSpark team will get in touch with you soon.
                </p>

                <button
                  type="button"
                  className="ms-success-close"
                  onClick={closePopup}
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                {/* =============================================
                    MODAL HEADER
                ============================================== */}
                <div className="ms-modal-header">
                  <div className="ms-modal-icon">
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        d="M4 5.5h16v11H8l-4 3v-14Z"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  <span className="ms-modal-tag">
                    GET STARTED
                  </span>

                  <h2 id="mindspark-demo-title">
                    Bring MindSpark to your school.
                  </h2>

                  <p>
                    Tell us how we can reach you and our team will help
                    you explore the right learning experience for your
                    students.
                  </p>
                </div>

                {/* =============================================
                    ERROR
                ============================================== */}
                {errorMessage && (
                  <div
                    className="ms-form-error"
                    role="alert"
                  >
                    <span className="ms-error-icon">!</span>

                    <span>{errorMessage}</span>
                  </div>
                )}

                {/* =============================================
                    FORM
                ============================================== */}
                <form
                  className="ms-contact-form"
                  onSubmit={handleSubmit}
                  noValidate
                >
                  <div className="ms-form-group">
                    <label htmlFor="demo-name">
                      Your Name
                    </label>

                    <div className="ms-input-wrapper">
                      <svg
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <circle
                          cx="12"
                          cy="8"
                          r="3.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        />

                        <path
                          d="M5.5 20c.5-4 3-6 6.5-6s6 2 6.5 6"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        />
                      </svg>

                      <input
                        id="demo-name"
                        type="text"
                        name="name"
                        placeholder="Enter your full name"
                        autoComplete="name"
                        required
                      />
                    </div>
                  </div>

                  <div className="ms-form-group">
                    <label htmlFor="demo-email">
                      Email Address
                    </label>

                    <div className="ms-input-wrapper">
                      <svg
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <rect
                          x="3"
                          y="5"
                          width="18"
                          height="14"
                          rx="2"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        />

                        <path
                          d="m4 7 8 6 8-6"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        />
                      </svg>

                      <input
                        id="demo-email"
                        type="email"
                        name="email"
                        placeholder="you@example.com"
                        autoComplete="email"
                        required
                      />
                    </div>
                  </div>

                  <div className="ms-form-group">
                    <label htmlFor="demo-mobile">
                      Mobile Number
                    </label>

                    <div className="ms-phone-wrapper">
                      <div className="ms-country-code">
                        +91
                      </div>

                      <input
                        id="demo-mobile"
                        type="tel"
                        name="mobile"
                        inputMode="numeric"
                        maxLength="10"
                        placeholder="10-digit mobile number"
                        autoComplete="tel"
                        required
                        onInput={(event) => {
                          event.currentTarget.value =
                            event.currentTarget.value.replace(
                              /[^0-9]/g,
                              ""
                            );
                        }}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="ms-submit-button"
                    disabled={isSending}
                  >
                    {isSending ? (
                      <>
                        <span className="ms-submit-spinner" />
                        Sending Request...
                      </>
                    ) : (
                      <>
                        Book My Free Demo

                        <svg
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path
                            d="M5 12h14M13 6l6 6-6 6"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </>
                    )}
                  </button>

                  <p className="ms-form-note">
                    By submitting, you agree to be contacted by the
                    MindSpark team regarding your enquiry.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}